import { Link } from 'react-router-dom';
import type { Product } from '@/mocks/products';
import { useApp } from '@/store/AppStore';

function formatPrice(price: number) {
  return `${price.toLocaleString('ko-KR')}원`;
}

export default function ProductCard({ product }: { product: Product }) {
  const { wishlist, toggleWishlist, addToCart } = useApp();
  const isWished = wishlist.includes(product.id);

  return (
    <div className="group relative flex flex-col rounded-2xl bg-background-100 border border-background-300 overflow-hidden transition-colors hover:border-background-500 data-product-shop">
      <Link
        to={`/products/${product.id}`}
        className="relative aspect-square overflow-hidden bg-background-200 cursor-pointer block"
      >
        <img
          src={product.image}
          alt={product.title}
          title={`${product.title} — ${product.category}`}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        {product.badge && (
          <span
            className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
              product.badge === '인기'
                ? 'bg-primary-500 text-foreground-950'
                : 'bg-accent-500 text-foreground-950'
            }`}
          >
            {product.badge}
          </span>
        )}
      </Link>

      <button
        type="button"
        onClick={() => toggleWishlist(product.id)}
        aria-label="찜하기"
        className={`absolute top-2 right-2 sm:top-3 sm:right-3 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full backdrop-blur-sm transition-all cursor-pointer ${
          isWished
            ? 'bg-accent-500 text-foreground-950'
            : 'bg-black/40 text-white/80 hover:bg-black/60 hover:text-white'
        }`}
      >
        <i className={isWished ? 'ri-heart-fill text-base' : 'ri-heart-line text-base'}></i>
      </button>

      <div className="flex flex-col gap-1.5 p-3 sm:p-4 flex-1">
        <Link to={`/products/${product.id}`} className="flex flex-col gap-1.5 cursor-pointer">
          <span className="text-xs font-mono uppercase tracking-wider text-primary-500 whitespace-nowrap">
            {product.category}
          </span>
          <h3 className="font-heading font-semibold text-foreground-100 leading-snug line-clamp-2 group-hover:text-white transition-colors">
            {product.title}
          </h3>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1 text-accent-500 font-semibold">
              <i className="ri-star-fill text-xs"></i>
              {product.rating}
            </span>
            <span>({product.reviews})</span>
            <span className="text-foreground-600">·</span>
            <span>판매 {product.sales}</span>
          </div>
        </Link>

        <div className="mt-auto pt-2 flex items-center justify-between">
          <span className="font-display font-semibold text-base sm:text-lg text-white">
            {formatPrice(product.price)}
          </span>
          <button
            type="button"
            onClick={() => addToCart(product.id)}
            aria-label="장바구니 담기"
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full bg-background-300 text-foreground-300 hover:bg-primary-500 hover:text-foreground-950 transition-colors cursor-pointer"
          >
            <i className="ri-shopping-cart-line text-base"></i>
          </button>
        </div>
      </div>
    </div>
  );
}